import { Link } from 'react-router-dom';
import LearnPage from './LearnPage';
import AbbrTip from '@/components/AbbrTip';

const FAQS = [
  {
    q: 'If my broker withholds the full 35% Swiss tax instead of 15%, can I get the difference back?',
    a: 'Yes, but only through a manual paper reclaim. Switzerland’s standard form is <strong>Form 82</strong>, which you mail to the Swiss Federal Tax Administration after the calendar year closes. The reclaim covers the 20-percentage-point gap between the 35% statutory rate and the 15% U.S. treaty rate. Expect a 6-to-18 month turnaround. A growing number of U.S. brokers (Fidelity, Schwab, Interactive Brokers) now apply the 15% treaty rate at source so you never have to file the reclaim — call your broker and ask before you buy the position.',
  },
  {
    q: 'Why does my F-share ticker get withholding tax but my Y-share ticker for the same company does not?',
    a: 'It does, on both. The suffix doesn’t change the underlying tax law — only the share-certificate plumbing. A Y-suffix ADR (e.g. NSRGY) and an F-suffix ordinary (e.g. NESNF) for the same Swiss company both face the same 35%-base / 15%-treaty Swiss withholding. The difference an investor actually feels is liquidity (ADRs are much tighter), a small ADR custodial fee ($0.01–$0.03 per share, deducted by the depository bank), and whether your broker handles the treaty reduction automatically. The tax math is identical.',
  },
  {
    q: 'Are foreign ordinary share dividends &quot;qualified&quot; for the lower U.S. tax rate?',
    a: 'Sometimes. To be qualified, the foreign company must either (a) be incorporated in a country with a comprehensive U.S. income-tax treaty <em>and</em> the treaty must include an information-exchange provision, OR (b) its shares (or ADRs) must be readily tradable on an established U.S. securities market. The first test catches most large EU / UK / Japanese / Australian / Canadian names. The second test is what saves NYSE-listed ADRs that come from non-treaty countries. The trap: very low-volume OTC Pink F-shares from non-treaty jurisdictions fail BOTH tests, so they’re taxed at your full ordinary rate (up to 37%) instead of the 0/15/20% qualified rate. Always cross-check before assuming a foreign dividend is qualified.',
  },
  {
    q: 'Does the Foreign Tax Credit actually erase the foreign withholding, or just reduce it?',
    a: 'For a typical retail investor in a taxable account, the Foreign Tax Credit (FTC) is a dollar-for-dollar reduction of your U.S. tax liability — so $1 of Swiss tax withheld erases $1 of U.S. tax owed. The catch: the credit is capped at the lesser of foreign tax paid or the U.S. tax that <em>would have been</em> owed on the same foreign income. For most middle-bracket investors the cap doesn’t bite — they get the full credit. High-rate foreign withholding (e.g. France’s 25% statutory rate if your broker doesn’t apply the treaty) can exceed the cap and the excess sits there as a carryover for 10 years. The credit also doesn’t recover the foreign tax — it offsets a separate U.S. tax bill — so the cash you sent overseas is gone unless you reclaim it with the source country directly.',
  },
  {
    q: 'Is there any country where my IRA gets the same 0% treatment as a Canadian dividend?',
    a: 'No. The U.S.–Canada treaty is the only one that explicitly recognizes U.S. retirement accounts as tax-exempt entities and waives the dividend withholding for IRAs and 401(k)s. Every other country (Switzerland, Germany, France, Italy, Japan, the Netherlands, etc.) will still withhold its full statutory or treaty rate from your IRA, and because the account itself pays no U.S. tax there is no Form 1116 credit available to recover it. That cash is permanently lost. For non-Canadian foreign dividend payers, a taxable brokerage account is almost always the correct location — the exact opposite of the standard &quot;put dividends in your Roth&quot; advice for U.S. holdings.',
  },
];

export default function LearnForeignOrdinarySharesTax() {
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: FAQS.map(({ q, a }) => ({
      '@type': 'Question',
      name: q.replace(/&quot;/g, '"'),
      acceptedAnswer: {
        '@type': 'Answer',
        text: a.replace(/<[^>]+>/g, '').replace(/&quot;/g, '"').replace(/&apos;/g, "'"),
      },
    })),
  };

  return (
    <LearnPage
      title="Foreign Ordinary Shares Tax Guide — F-Shares, Y-Shares, Withholding & the FTC (2026) | DiviDrip"
      description="How foreign ordinary shares (F-shares) and ADRs (Y-shares) are taxed on U.S. brokerage accounts: statutory withholding by country, U.S. treaty rates, Foreign Tax Credit mechanics, IRA placement traps, and the 2026 qualified-dividend interaction."
      canonical="https://twylightcrow.com/learn/foreign-ordinary-shares-tax"
      guidePath="/learn/foreign-ordinary-shares-tax"
      sectionLabel="Dividend Taxes"
      sectionAnchor="tax"
      kicker="Learn · Dividend taxes"
      h1="Foreign Ordinary Shares & Dividend Withholding — The 2026 Tax Guide"
      datePublished="2026-06-10"
      jsonLd={jsonLd}
      related={[
        { to: '/learn/qualified-vs-ordinary-dividends', label: 'Qualified vs ordinary dividends — 2026 tax guide' },
        { to: '/learn/niit-surtax', label: 'Navigating the NIIT — the 3.8% surtax guide' },
        { to: '/learn/why-drip-roth', label: 'Why DRIP in your Roth, not your taxable' },
        { to: '/learn/tax-loss-harvesting-playbook', label: 'The tax-loss harvesting playbook' },
        { to: '/learn/international-dividends', label: 'Why international dividends matter' },
        { to: '/learn/ex-us-dividend-aristocrats', label: 'Ex-U.S. dividend aristocrats' },
        { to: '/glossary#entry-tax-treaty-rate', label: 'Glossary: Tax Treaty Rate' },
        { to: '/glossary#entry-foreign-withholding-tax-wht', label: 'Glossary: Foreign Withholding Tax (WHT)' },
        { to: '/glossary#entry-adr-tax-drag', label: 'Glossary: ADR Tax Drag' },
        { to: '/screener', label: 'Stock Screener — filter by yield, sector, frequency' },
      ]}
    >
      <p>
        When you buy a foreign company on a U.S. brokerage screen, the dividend that
        lands in your cash account is almost never the dividend the company actually
        declared. A foreign government has already taken its cut <em>before</em> the
        money reaches you, and the size of that cut depends on three things: the
        company’s home country, whether your broker applies the U.S. tax-treaty
        rate at source, and whether you hold the position in a taxable account or an
        IRA. This guide walks through the full mechanics — and the account-placement
        decisions that follow.
      </p>

      <h2>F-shares vs Y-shares — what the ticker suffix tells you</h2>
      <p>
        Foreign companies reach U.S. investors through two different ticker
        structures. Both face the same underlying foreign withholding tax — the
        difference matters mostly for liquidity, fees, and execution mechanics.
      </p>
      <table className="learn-table">
        <thead>
          <tr>
            <th className="text-left font-semibold">Type</th>
            <th className="text-left font-semibold">Example</th>
            <th className="text-left font-semibold">What it is</th>
            <th className="text-left font-semibold">Liquidity</th>
            <th className="text-left font-semibold">Extra fees</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td className="font-semibold">Y-suffix ADR</td>
            <td>NSRGY (Nestlé)</td>
            <td>Certificate issued by a U.S. depository bank (JPMorgan, BNY Mellon, Citi) representing a fixed ratio of underlying foreign shares.</td>
            <td>High. Sponsored ADRs trade actively on U.S. exchanges or the OTCQX tier.</td>
            <td>ADR custodial fee — typically $0.01–$0.03 per share, deducted by the depository.</td>
          </tr>
          <tr>
            <td className="font-semibold">F-suffix ordinary</td>
            <td>PGPHF (Partners Group)</td>
            <td>The raw foreign-listed ordinary share, traded directly on the U.S. OTC Pink market in USD.</td>
            <td>Low. Most price discovery happens on the home exchange (e.g. SIX Swiss as PGHN).</td>
            <td>None from a depository, but wider spreads can cost more than the ADR fee saves.</td>
          </tr>
          <tr>
            <td className="font-semibold">U.S.-listed direct</td>
            <td>ASML, TM, ENB, SHEL</td>
            <td>Foreign companies that list directly on the NYSE or Nasdaq under a standard 2–4 letter ticker. Most are technically ADRs underneath, but feel exactly like a U.S. stock.</td>
            <td>Highest. Often more volume in the U.S. than on the home exchange.</td>
            <td>Usually none — many direct listings absorb the depository fee into the corporate budget.</td>
          </tr>
        </tbody>
      </table>
      <p>
        <strong>The trap:</strong> the &quot;Zero Suffix&quot; effect. Because ASML,
        TM, ENB, and SHEL look and trade like American stocks, many investors are
        blindsided when their 1099-DIV shows foreign tax withheld in Box 7. The tax
        treatment follows the company’s country of incorporation — not the
        ticker length.
      </p>

      <h2>Statutory vs treaty withholding rates by country (2026)</h2>
      <p>
        Each country sets its own statutory withholding rate on outbound dividends.
        The U.S. has bilateral tax treaties with most major economies that{' '}
        <em>reduce</em> the rate for U.S. resident portfolio investors — but only if
        your broker applies the treaty rate at source. If they don’t, you’re
        stuck with the higher statutory number until you file a reclaim with the
        foreign government.
      </p>
      <table className="learn-table">
        <thead>
          <tr>
            <th className="text-left font-semibold">Country</th>
            <th className="text-left font-semibold">Statutory rate</th>
            <th className="text-left font-semibold">U.S. treaty rate</th>
            <th className="text-left font-semibold">Notable holdings</th>
            <th className="text-left font-semibold">Notes</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td className="font-semibold">🇨🇭 Switzerland</td>
            <td>35%</td>
            <td>15%</td>
            <td>NSRGY, NESNF, PGPHF, ROG.SW, NOVN.SW</td>
            <td>Most aggressive baseline rate in the developed world. Reclaim via Form 82 if the broker doesn’t apply 15% automatically.</td>
          </tr>
          <tr>
            <td className="font-semibold">🇩🇪 Germany</td>
            <td>26.375%</td>
            <td>15%</td>
            <td>BMWYY, BAMXF, SIEGY, BASFY, ALV.DE</td>
            <td>25% base + 5.5% solidarity surcharge on the tax (= 1.375%). Treaty reduction usually applied at source.</td>
          </tr>
          <tr>
            <td className="font-semibold">🇫🇷 France</td>
            <td>25%</td>
            <td>15%</td>
            <td>LRLCY, LRLCF, BNPQY, TTE, AIRYY</td>
            <td>Source reduction requires U.S. residency form (W-8BEN) on file with the broker.</td>
          </tr>
          <tr>
            <td className="font-semibold">🇮🇹 Italy</td>
            <td>26%</td>
            <td>15%</td>
            <td>ENLAY, ESOCF, ISNPY, UCG.MI</td>
            <td>Treaty reduction commonly applied automatically.</td>
          </tr>
          <tr>
            <td className="font-semibold">🇳🇱 Netherlands</td>
            <td>15%</td>
            <td>15%</td>
            <td>ASML, RDS.A heritage, RDDUY (formerly), HEINY</td>
            <td>Statutory rate equals the treaty rate — clean and predictable.</td>
          </tr>
          <tr>
            <td className="font-semibold">🇯🇵 Japan</td>
            <td>15.315%</td>
            <td>10%</td>
            <td>TM, TOYOF, SNE, HMC, MUFG, NMR</td>
            <td>One of the lowest U.S. treaty rates anywhere. 15.315% base = 15% national + 0.315% reconstruction surcharge.</td>
          </tr>
          <tr>
            <td className="font-semibold">🇨🇦 Canada</td>
            <td>25%</td>
            <td>15% (taxable) / <strong>0% (IRA)</strong></td>
            <td>ENB, BNS, RY, TD, BCE, BMO, MFC</td>
            <td>The only treaty that explicitly waives withholding for U.S. retirement accounts. Holding Canadian dividends inside an IRA = 0% tax at any layer.</td>
          </tr>
          <tr>
            <td className="font-semibold">🇬🇧 United Kingdom</td>
            <td><strong>0%</strong></td>
            <td>—</td>
            <td>SHEL, AZN, DEO, UL, BTI, GSK, HSBC</td>
            <td>No outbound dividend withholding for non-resident investors. Exception: UK REIT property income distributions (PIDs) carry a 20% withholding.</td>
          </tr>
          <tr>
            <td className="font-semibold">🇸🇬 Singapore</td>
            <td><strong>0%</strong></td>
            <td>—</td>
            <td>SINGY, ST Eng., DBS, OCBC</td>
            <td>Single-tier corporate tax system; no separate withholding levied.</td>
          </tr>
          <tr>
            <td className="font-semibold">🇦🇺 Australia</td>
            <td><strong>0%</strong> on franked / 30% on unfranked</td>
            <td>15% (unfranked portion)</td>
            <td>BHP, RIO, WBK, NAB.AX</td>
            <td>Franked dividends (corporate tax already paid) escape withholding entirely. The unfranked portion on your statement still gets hit.</td>
          </tr>
          <tr>
            <td className="font-semibold">🇧🇷 Brazil</td>
            <td><strong>0%</strong> on dividends / 15% on Interest-on-Equity (JCP)</td>
            <td>—</td>
            <td>VALE, ITUB, PBR, BBD</td>
            <td>Pure dividends are untaxed at source. The JCP portion (a Brazilian quirk classified as interest, not dividend) gets withheld at 15%.</td>
          </tr>
        </tbody>
      </table>
      <p className="text-xs text-muted-foreground italic">
        Sources: country-level statutory rates are published by each country’s
        tax authority (Swiss Federal Tax Administration, German Bundeszentralamt
        für Steuern, etc.) and summarized in the U.S. Treasury’s bilateral
        treaty schedules. Treaty reductions assume you are a U.S. resident
        portfolio investor (not a substantial holder or active beneficiary). Your
        broker must have a current W-8BEN on file to claim treaty rates at source.
      </p>

      <h2>The two-layer tax stack</h2>
      <p>
        Owning a foreign dividend stock in a U.S. taxable account means surviving{' '}
        <strong>two separate tax events</strong> on the same dollar of dividend cash:
      </p>
      <ol>
        <li>
          <strong>Layer 1 — Foreign withholding.</strong> Taken at source before the
          dividend ever reaches your broker. You see only the net amount in your
          cash balance.
        </li>
        <li>
          <strong>Layer 2 — U.S. federal tax.</strong> The IRS taxes the{' '}
          <em>gross</em> dividend (Box 1a on your 1099-DIV), not the net you
          actually received. To avoid double taxation, you claim the{' '}
          <strong>Foreign Tax Credit (FTC)</strong> for the amount in Box 7 of the
          same 1099-DIV.
        </li>
      </ol>
      <p>
        The U.S. layer is then taxed at one of two completely different rate
        schedules depending on whether the dividend is <em>qualified</em>:
      </p>

      <h3>Qualified dividend rates — 2026 brackets</h3>
      <p>
        If the foreign company is incorporated in a treaty country (or its shares
        trade on a U.S. exchange) AND you held the stock for more than 60 days in
        the 121-day window around the ex-dividend date, the dividend qualifies for
        the preferential long-term capital gains rate:
      </p>
      <table className="learn-table">
        <thead>
          <tr>
            <th className="text-left font-semibold">Filing status</th>
            <th className="text-left font-semibold">0% rate</th>
            <th className="text-left font-semibold">15% rate</th>
            <th className="text-left font-semibold">20% rate</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td className="font-semibold">Single / Head of Household</td>
            <td>Taxable income ≤ $49,450</td>
            <td>$49,451 – $545,500</td>
            <td>over $545,500</td>
          </tr>
          <tr>
            <td className="font-semibold">Married Filing Jointly</td>
            <td>Taxable income ≤ $98,900</td>
            <td>$98,901 – $613,700</td>
            <td>over $613,700</td>
          </tr>
          <tr>
            <td className="font-semibold">Married Filing Separately</td>
            <td>Taxable income ≤ $49,450</td>
            <td>$49,451 – $306,850</td>
            <td>over $306,850</td>
          </tr>
        </tbody>
      </table>
      <p className="text-xs text-muted-foreground italic">
        Source: IRS Revenue Procedure 2025-32 (annual inflation adjustments for tax
        year 2026). Brackets shift slightly each year — always verify against
        irs.gov before filing.
      </p>

      <h3>Ordinary dividend rates — the fallback when &quot;qualified&quot; fails</h3>
      <p>
        Low-volume OTC F-shares from non-treaty countries, dividends held for less
        than 60 days, and most REIT/BDC distributions get pushed into your
        standard ordinary-income brackets — peaking at <strong>37%</strong>{' '}
        federally before any state tax. That gap between 15% qualified and 37%
        ordinary is the single largest after-tax-yield variable on a foreign
        position. Verify qualified status <em>before</em> committing capital.
      </p>

      <h2>The Foreign Tax Credit — how it actually works</h2>
      <p>
        The FTC is the IRS’s mechanism for keeping you from getting taxed
        twice on the same dividend. The mechanics:
      </p>
      <ul>
        <li>
          <strong>Simplified election (most retail investors).</strong> If your
          total foreign taxes paid across all positions is{' '}
          <strong>under $300 ($600 MFJ)</strong>, you can claim the credit
          directly on Schedule 3 of your Form 1040 with no extra paperwork.
          No <strong>Form 1116</strong> required.
        </li>
        <li>
          <strong>Form 1116 (above the threshold).</strong> Once you cross
          $300/$600, the IRS makes you file Form 1116, which caps the credit at
          the lesser of foreign tax paid OR the U.S. tax you would have owed on
          that same foreign-source income. The cap rarely bites for middle-bracket
          investors but can leave high-rate foreign withholdings (e.g. France’s
          25% statutory rate before treaty reduction) partially unrecovered. The
          excess carries forward up to 10 years.
        </li>
        <li>
          <strong>Credit vs deduction.</strong> You can choose to either
          credit foreign taxes (dollar-for-dollar against U.S. liability) OR
          deduct them as an itemized deduction. The credit is almost always
          better — you don’t have to itemize, and a dollar of credit beats
          a dollar of deduction at any tax rate.
        </li>
      </ul>

      <h2>The IRA trap — where most investors lose money</h2>
      <p>
        Conventional wisdom says &quot;put your highest-yielding dividend stocks in
        your Roth.&quot; For foreign positions, that wisdom is{' '}
        <em>often dangerously wrong</em>:
      </p>
      <ul>
        <li>
          <strong>The Foreign Tax Credit only exists against U.S. tax.</strong>{' '}
          Inside an IRA, you owe zero U.S. tax on the dividend — so there’s
          no U.S. liability for the FTC to offset. The foreign tax that came out
          at source is permanently lost. Switzerland still takes its 15%, but you
          get nothing back.
        </li>
        <li>
          <strong>Canada is the lone exception.</strong> The U.S.–Canada treaty
          explicitly recognizes IRAs as tax-exempt entities and waives the 15%
          Canadian withholding. Holding ENB, BNS, or RY inside an IRA = 0% tax
          at every layer.
        </li>
        <li>
          <strong>The right call for non-Canadian foreign dividends:</strong>{' '}
          taxable brokerage. You give up the IRA’s tax shield, but you
          recover the foreign withholding through the FTC. Net result: only the
          U.S. qualified-dividend rate (typically 15%) bites — vs the full
          15%+ of foreign withholding burning in a Roth.
        </li>
      </ul>

      <h2>The NIIT layer on top</h2>
      <p>
        High-income investors face a third tax: the 3.8%{' '}
        <Link to="/learn/niit-surtax"><AbbrTip term="NIIT">Net Investment Income Tax</AbbrTip></Link>.
        It hits when your{' '}
        <AbbrTip term="MAGI">Modified Adjusted Gross Income</AbbrTip> exceeds{' '}
        <strong>$200,000 (Single)</strong> or <strong>$250,000 (MFJ)</strong>.
        Foreign dividends — whether qualified or ordinary — count as Net
        Investment Income and trigger NIIT on the same terms as U.S. dividends.
        The thresholds are not indexed for inflation. The Foreign Tax Credit{' '}
        <em>does not</em> offset NIIT — only your regular income tax — so a 15%
        Swiss withholding stacked under U.S. 15% qualified + 3.8% NIIT can push
        the effective rate well past 30% before state tax.
      </p>
      <p>
        IRA-held foreign dividends sidestep NIIT entirely while they remain
        inside the account, but the foreign withholding loss usually outweighs
        that savings — except for Canadian holdings, which escape both layers.
      </p>

      <h2>Worked example — PGPHF in a taxable account</h2>
      <p>
        Suppose a U.S. investor (MFJ, $180,000 taxable income) holds 100 shares
        of <strong>Partners Group Holding AG (PGPHF)</strong>, the OTC Pink
        F-share representing direct Swiss ordinary shares. Partners Group
        declares a CHF 36.00 / share annual dividend (≈ $40.00 USD on payment
        date):
      </p>
      <ul>
        <li>Gross dividend: <strong>$4,000</strong> (100 sh × $40).</li>
        <li>
          Swiss withholding at the 15% treaty rate (broker applies it
          automatically):{' '}
          <strong>−$600</strong>. Net cash deposited: $3,400.
        </li>
        <li>
          U.S. federal tax: the IRS taxes the <em>gross</em> $4,000. PGPHF is
          incorporated in Switzerland (treaty country), so the dividend is{' '}
          <em>qualified</em> assuming the 60-day holding rule is met. At
          $180,000 MFJ income, the qualified rate is 15%: <strong>−$600</strong>.
        </li>
        <li>
          Foreign Tax Credit: the $600 Swiss withholding offsets the $600 U.S.
          tax bill on the dividend → <strong>net U.S. tax owed: $0</strong>.
        </li>
        <li>
          <strong>Final after-tax dividend: $3,400</strong> (gross $4,000 minus
          the $600 Swiss layer that the FTC could not actually recover — it
          only zeroed out the U.S. obligation).
        </li>
      </ul>
      <p>
        Compare to the IRA version of the same trade: gross $4,000 → Swiss takes
        $600 → IRA pays no U.S. tax → no FTC available → final after-tax
        dividend $3,400. <em>The same outcome.</em> No win from the IRA wrapper
        — and the investor used precious tax-sheltered space for a position
        that didn’t benefit from it.
      </p>
      <p>
        Now imagine the investor had picked a comparable Canadian dividend
        (Enbridge, ENB) yielding the same $4,000 gross. In a taxable account:
        $4,000 → 15% Canadian withholding ($600) → 15% U.S. qualified tax
        ($600) → FTC offsets the U.S. tax → net $3,400. In an IRA: $4,000 → 0%
        Canadian withholding (treaty waiver) → 0% U.S. tax → net{' '}
        <strong>$4,000</strong>. The Canadian dividend captures $600 of extra
        income just by being in the right wrapper.
      </p>

      <h2>The placement playbook</h2>
      <ol>
        <li>
          <strong>Canadian dividends → IRA.</strong> Only foreign country with
          the IRA waiver. ENB, BNS, RY, TD, BMO, MFC all benefit dramatically.
        </li>
        <li>
          <strong>UK / Singapore / Australian-franked / pure-Brazil →
          either, but slight edge to Roth.</strong> Zero foreign withholding
          means no FTC needed. Inside a Roth, you also avoid U.S. qualified
          tax and NIIT entirely.
        </li>
        <li>
          <strong>European / Japanese / non-Canadian withholding-heavy →
          taxable brokerage.</strong> Keeps the Foreign Tax Credit pathway
          alive. The credit is the only mechanism that prevents real
          double-taxation.
        </li>
        <li>
          <strong>Low-volume OTC F-shares from non-treaty countries →
          avoid for income.</strong> No qualified-dividend status (taxed at
          ordinary rates up to 37%), no treaty rate reduction, and reclaim
          paperwork that can take 18 months. Use only if you have a structural
          reason to own the specific share class.
        </li>
      </ol>

      <h2>FAQ</h2>
      <dl className="learn-faq">
        {FAQS.map(({ q, a }) => (
          <div key={q} className="my-4">
            <dt
              className="font-semibold text-foreground"
              dangerouslySetInnerHTML={{ __html: q }}
            />
            <dd
              className="mt-1 text-foreground/85"
              dangerouslySetInnerHTML={{ __html: a }}
            />
          </div>
        ))}
      </dl>

      <p>
        Once you know what your broker is withholding and what country a position
        comes from, the placement decision usually becomes obvious. For the
        broader account-placement framework, see{' '}
        <Link to="/learn/why-drip-roth">Why DRIP in your Roth, not your taxable</Link>{' '}
        and the companion{' '}
        <Link to="/learn/qualified-vs-ordinary-dividends">qualified vs ordinary dividends guide</Link>{' '}
        for the U.S. side of the picture.
      </p>

      <p className="text-xs text-muted-foreground italic mt-6">
        Disclaimer: This is not tax advice. Foreign withholding rates, treaty
        terms, and IRS rules change frequently — and your specific broker’s
        treaty processing can differ from the typical case described here. Always
        verify the rate your broker is actually applying (call them or check a
        recent 1099-DIV) and consult a CPA or Enrolled Agent before making
        decisions on positions large enough to matter.
      </p>
    </LearnPage>
  );
}
